import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { TypographyType } from './consts';
import * as S from './TypographyStyles';

/** ALLOW SPREADING: since this is simple div/h* element, and we might want to assign onClick, onMouse**, className, and many more */
/* eslint-disable react/jsx-props-no-spreading */
export const Typography = ({ type, weight, textTransform, testId, color, children, ...props }) => {
  const Comp = S[type];
  return Comp ? (
    <Comp
      $weight={weight}
      $textTransform={textTransform}
      data-testid={testId ?? props['data-testid']}
      $color={color}
      {...props}>
      {children}
    </Comp>
  ) : (
    children
  );
};

Typography.propTypes = {
  type: PropTypes.oneOf(['H1', 'H2', 'H3', 'H4', 'B1', 'B1C', 'B1Condensed', 'B2', 'B2C', 'B2Condensed']).isRequired,
  weight: PropTypes.oneOf(['EXTRA_BOLD', 'BOLD', 'SEMI_BOLD', 'REGULAR', 'LIGHT', 'THIN', 'EXTRA_THIN']),
  textTransform: PropTypes.oneOf(['lowercase', 'capitalize', 'uppercase', 'none', 'unset']),
  testId: PropTypes.string,
  'data-testid': PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};

export const H1 = ({ weight, children, ...props }) => (
  <Typography type={TypographyType.H1} weight={weight} {...props}>
    {children}
  </Typography>
);
H1.propTypes = {
  weight: PropTypes.oneOf(['SEMI_BOLD', 'REGULAR']),
  textTransform: PropTypes.oneOf(['lowercase', 'capitalize', 'uppercase', 'none', 'unset']),
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.H1 = H1;

export const H2 = ({ weight, children, ...props }) => (
  <Typography type={TypographyType.H2} weight={weight} {...props}>
    {children}
  </Typography>
);
H2.propTypes = {
  weight: PropTypes.oneOf(['BOLD', 'SEMI_BOLD']),
  textTransform: PropTypes.oneOf(['lowercase', 'capitalize', 'uppercase', 'none', 'unset']),
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.H2 = H2;

export const H3 = ({ weight, children, ...props }) => (
  <Typography type={TypographyType.H3} weight={weight} {...props}>
    {children}
  </Typography>
);
H3.propTypes = {
  weight: PropTypes.oneOf(['BOLD', 'SEMI_BOLD', 'REGULAR']),
  textTransform: PropTypes.oneOf(['lowercase', 'capitalize', 'uppercase', 'none', 'unset']),
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.H3 = H3;

export const H4 = ({ weight, children, ...props }) => (
  <Typography type={TypographyType.H4} weight={weight} {...props}>
    {children}
  </Typography>
);
H4.propTypes = {
  weight: PropTypes.oneOf(['BOLD', 'REGULAR']),
  textTransform: PropTypes.oneOf(['lowercase', 'capitalize', 'uppercase', 'none', 'unset']),
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.H4 = H4;

export const B1 = ({ weight, children, ...props }) => (
  <Typography type={TypographyType.B1} weight={weight} {...props}>
    {children}
  </Typography>
);
B1.propTypes = {
  weight: PropTypes.oneOf(['BOLD', 'SEMI_BOLD', 'REGULAR']),
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.B1 = B1;

export const B1C = ({ children, ...props }) => (
  <Typography type={TypographyType.B1C} {...props}>
    {children}
  </Typography>
);
B1C.propTypes = {
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.B1C = B1C;

export const B2 = ({ weight, children, ...props }) => (
  <Typography type={TypographyType.B2} weight={weight} {...props}>
    {children}
  </Typography>
);
B2.propTypes = {
  weight: PropTypes.oneOf(['EXTRA_BOLD', 'BOLD']),
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.B2 = B2;

export const B2C = ({ weight, children, ...props }) => (
  <Typography type={TypographyType.B2C} weight={weight} {...props}>
    {children}
  </Typography>
);
B2C.propTypes = {
  weight: PropTypes.oneOf(['SEMI_BOLD', 'REGULAR', 'LIGHT']),
  testId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node
};
Typography.B2C = B2C;

export const Error = styled(B2C)`
  position: absolute;
  bottom: -15px;
  right: 0;
  color: var(--error);
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
